import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import StockAdjustmentModal from "../../core/modals/stock/StockAdjustmentModal";
import { fetchStockAdjustments, deleteStockAdjustmentThunk } from "../../core/redux/slices/stockSlice";
import { getApiErrorMessage } from "../../core/utils/apiError";

const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—";

const StockAdjustment = () => {
  const dispatch = useDispatch();
  const { adjustments, loading } = useSelector((s) => s.stock);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => { dispatch(fetchStockAdjustments()); }, [dispatch]);
  useEffect(() => { load(); }, [load]);

  const data = useMemo(() => {
    const q = search.toLowerCase();
    return adjustments.filter(
      (r) => !q
        || r.adjustmentEntryNo?.toLowerCase().includes(q)
        || r.reason?.toLowerCase().includes(q)
        || r.remarks?.toLowerCase().includes(q)
    );
  }, [adjustments, search]);

  const handleDelete = (record) => {
    const id = record.adjustmentID ?? record.id;
    Swal.fire({
      title: "Delete Adjustment?",
      text: `Entry ${record.adjustmentEntryNo || id} will be removed and stock levels restored.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await dispatch(deleteStockAdjustmentThunk(id)).unwrap();
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Adjustment deleted", showConfirmButton: false, timer: 2000 });
      } catch (err) {
        Swal.fire("Error", getApiErrorMessage(err, "Failed to delete adjustment."), "error");
      }
    });
  };

  const columns = [
    {
      title: "Entry No", dataIndex: "adjustmentEntryNo", width: 160,
      sorter: (a, b) => (a.adjustmentEntryNo || "").localeCompare(b.adjustmentEntryNo || ""),
    },
    {
      title: "Date", dataIndex: "adjustmentDate", width: 120,
      render: fmtDate,
      sorter: (a, b) => (a.adjustmentDate || "").localeCompare(b.adjustmentDate || ""),
    },
    { title: "Reason", dataIndex: "reason", width: 120 },
    { title: "Remarks", dataIndex: "remarks", ellipsis: true },
    {
      title: "", dataIndex: "action", width: 60, align: "center",
      render: (_, record) => (
        <button className="btn btn-sm text-danger p-1" title="Delete" onClick={() => handleDelete(record)}>
          <Trash2 size={15} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Stock Adjustments" subtitle="Record manual stock corrections" onAdd={() => setShowModal(true)} addLabel="New Adjustment" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search entry no, reason or remarks..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={load}>Refresh</button>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <StockAdjustmentModal show={showModal} onClose={() => { setShowModal(false); load(); }} />
    </div>
  );
};

export default StockAdjustment;
